import React, { useEffect, useMemo, useState } from "react";

/**
 * Help modal = quick coaching panel (NOT a lesson page).
 * Update goals:
 * - “Focus” becomes “Challenge” (exercise-specific, memorable slogan)
 * - Rhythm patterns must be visible (not decorative)
 * - Avoid duplication: if Targets exist, don’t repeat Examples
 * - Student tab: Challenge → Targets → If stuck
 * - Teacher tab: Challenge → Fixes → Drills → Extra
 */

// ---------- tiny primitives ----------

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={[
        "h-10 px-3 rounded-xl text-sm font-medium border transition",
        active
          ? "bg-white text-gray-900 border-gray-300 shadow-sm"
          : "bg-white/50 text-gray-600 border-white/40 hover:bg-white/70",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// More visible pattern chip (not “metadata”)
function PatternTag({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold tracking-wide bg-white border border-gray-300 text-gray-900 shadow-sm">
      {children}
    </span>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={[
        "rounded-2xl border border-white/60 bg-white/75 backdrop-blur-sm shadow-sm",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function CardHeader({ kicker, title, right }) {
  return (
    <div className="px-5 pt-5 pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          {kicker && <div className="text-xs text-gray-500">{kicker}</div>}
          {title && <div className="text-base font-bold text-gray-900">{title}</div>}
        </div>
        {right}
      </div>
    </div>
  );
}

function CardBody({ children }) {
  return <div className="px-5 pb-5">{children}</div>;
}

function BigCue({ title, subtitle }) {
  return (
    <div className="text-center space-y-1">
      <div className="text-[20px] sm:text-[24px] font-semibold text-gray-900 leading-snug">
        {title}
      </div>
      {subtitle && <div className="text-sm text-gray-600">{subtitle}</div>}
    </div>
  );
}

function Targets({ lines = [] }) {
  if (!lines.length) return null;
  return (
    <div className="space-y-2">
      {lines.map((line, idx) => (
        <div key={idx} className="rounded-xl border border-white/60 bg-white/60 px-4 py-3">
          <div className="font-mono text-lg sm:text-xl text-gray-900 leading-relaxed whitespace-pre-wrap">
            {line}
          </div>
        </div>
      ))}
    </div>
  );
}

function Disclosure({ title, subtitle, open, onToggle, children }) {
  return (
    <div className="rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <div>
          <div className="font-bold text-gray-900">{title}</div>
          {subtitle && <div className="text-sm text-gray-600">{subtitle}</div>}
        </div>
        <div
          className={[
            "w-9 h-9 rounded-xl border border-white/70 bg-white/60 flex items-center justify-center transition-transform",
            open ? "rotate-180" : "rotate-0",
          ].join(" ")}
          aria-hidden="true"
        >
          <span className="text-gray-700">⌄</span>
        </div>
      </button>

      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// ---------- modal ----------

export default function ExerciseHelpModal({
  open,
  onClose,
  exercise,
  course,
  initialTab = "student",
  mode = "exercise",
}) {
  const [tab, setTab] = useState(initialTab);

  // Separate disclosures
  const [stuckOpen, setStuckOpen] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);

  const hasExtra = (exercise?.extraSpecific?.length ?? 0) > 0;

  // Teacher content
  const teacher = exercise?.teacher ?? {};
  const challengeList = teacher?.challenge ?? teacher?.focus ?? [];
  const fixes = teacher?.articulation ?? [];
  const drills = teacher?.actions ?? [];
  const extraExercises = teacher?.extraExercises ?? [];
  const hasAbout =
  !!course?.AboutPanel &&
  !!course?.metadata?.help?.aboutEnabled;

  // Reset tab + disclosures when exercise changes
  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setStuckOpen(false);
    setExtraOpen(false);
  }, [open, exercise?.id, initialTab]);

  // Lock body scroll + ESC
  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

const title = useMemo(() => {
  if (mode === "about") return "About the course";
  if (exercise?.id) return `Exercise ${exercise.id} – What to focus on`;
  return "Exercise – What to focus on";
}, [mode, exercise?.id]);

  const kicker = useMemo(() => {
    const author = course?.metadata?.author ? `by ${course.metadata.author}` : "Guidelines";
    const edition = course?.metadata?.edition ? ` • ${course.metadata.edition}` : "";
    return `${author}${edition}`;
  }, [course?.metadata?.author, course?.metadata?.edition]);

  // Student anchor (slogan) — prefer explicit, otherwise fall back to a sane default.
  const studentAnchor = useMemo(() => {
    // expected shape:
    // exercise.studentAnchor = { title: "Two beats, one chain.", subtitle: "Don’t restart in the middle." }
    if (exercise?.studentAnchor?.title) return exercise.studentAnchor;

    // fallback: try to infer from challengeList (first 1–2 bullets) ONLY if you want.
    // Safer default:
    return {
      title: "Two beats, one chain.",
      subtitle: "Don’t restart in the middle.",
    };
  }, [exercise?.studentAnchor, challengeList]);

  if (!open) return null;

  // ================================
// ABOUT MODE (no exercise logic)
// ================================
if (mode === "about") {
  const About = course?.AboutPanel || null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onPointerDown={() => onClose?.()}
      />
      <div
        className="absolute inset-0 flex items-center justify-center p-4"
        onPointerDown={() => onClose?.()}
      >
        <div
          onPointerDown={(e) => e.stopPropagation()}
          className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <div className="text-lg font-bold">
              {course?.metadata?.title || "About"}
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg border bg-white hover:bg-gray-50"
            >
              ×
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            {About ? (
              <About />
            ) : (
              <div className="text-sm text-gray-600 text-center">
                No About panel available.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
  // Fallback if exercise missing
  if (!exercise) {
    return (
      <div className="fixed inset-0 z-[60]">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onPointerDown={() => onClose?.()} />
        <div className="absolute inset-0 flex items-center justify-center p-4" onPointerDown={() => onClose?.()}>
          <div
            onPointerDown={(e) => e.stopPropagation()}
            className="w-full max-w-xl rounded-2xl bg-white/90 border border-white/60 shadow-2xl overflow-hidden"
          >
            <div className="p-5 border-b border-white/60 flex items-center justify-between">
              <div className="font-bold text-gray-900">Exercise help</div>
              <button
                onClick={onClose}
                type="button"
                className="w-10 h-10 rounded-xl bg-white/70 border border-white/60 hover:bg-white flex items-center justify-center"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="text-gray-800 font-medium">No help available for this exercise yet.</div>
              <div className="text-sm text-gray-500 mt-2">(Course loaded, but the exercise id didn’t match.)</div>
              <button
                onClick={onClose}
                type="button"
                className="mt-5 px-4 py-2 rounded-xl bg-white/80 border border-white/60 hover:bg-white text-sm font-medium text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const Glossary = course?.GlossaryPanel || null;
  const hasTargets = (exercise.studentCore?.length ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onPointerDown={(e) => {
          e.preventDefault();
          onClose?.();
        }}
      />

      {/* Click-catcher */}
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-4" onPointerDown={() => onClose?.()}>
        {/* Panel */}
        <div
          onPointerDown={(e) => e.stopPropagation()}
          className={[
            "w-full max-w-3xl",
            "bg-gradient-to-br from-blue-50 via-white to-purple-50",
            "border border-white/60 shadow-2xl overflow-hidden",
            "rounded-none sm:rounded-2xl",
            "flex flex-col",
            "max-h-[100svh] sm:max-h-[90svh]",
          ].join(" ")}
        >
          {/* Header */}
          <div className="sticky top-0 z-20 bg-white/70 backdrop-blur-sm border-b border-white/60">
            <div className="px-5 py-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-gray-500">{kicker}</div>
                <div className="text-lg sm:text-xl font-bold text-gray-900">{title}</div>
              </div>

              <button
                onClick={onClose}
                type="button"
                className="shrink-0 w-10 h-10 rounded-xl bg-white/70 border border-white/60 hover:bg-white flex items-center justify-center text-gray-700"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Tabs + hint */}
            <div className="px-5 pb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2 overflow-x-auto pb-1 pr-2 [-webkit-overflow-scrolling:touch]">
                <TabButton active={tab === "student"} onClick={() => setTab("student")}>
                  Student
                </TabButton>
                <TabButton active={tab === "teacher"} onClick={() => setTab("teacher")}>
                  Teacher
                </TabButton>
                <TabButton active={tab === "glossary"} onClick={() => setTab("glossary")}>
                  Glossary
                </TabButton>
                {hasAbout && (
  <TabButton active={tab === "about"} onClick={() => setTab("about")}>
    {course?.metadata?.help?.aboutLabel || "About"}
  </TabButton>
)}
              </div>
              <div className="text-xs text-gray-500">ESC closes • Click outside to close</div>
            </div>
          </div>

          {/* Sticky rhythm row (always visible while scrolling) */}
          <div className="sticky top-[112px] z-10 bg-gradient-to-br from-blue-50/80 via-white/80 to-purple-50/80 backdrop-blur-sm border-b border-white/60">
            <div className="px-5 py-3">
              <div className="flex flex-wrap items-center gap-2">
                {(exercise.patterns || []).map((p) => (
                  <PatternTag key={p}>{p}</PatternTag>
                ))}
              </div>
              <div className="mt-1 text-xs text-gray-600">Clap on X • keep x light</div>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 sm:p-6 overflow-y-auto flex-1 min-h-0 overscroll-contain [-webkit-overflow-scrolling:touch]">
            {/* Optional examples ONLY if there are no targets (avoid duplication) */}
            {!hasTargets && (exercise.examples?.length ?? 0) > 0 && (
              <div className="mb-4 text-xs text-gray-500">
                Examples: {(exercise.examples ?? []).slice(0, 3).join(" · ")}
              </div>
            )}

            {/* STUDENT */}
            {tab === "student" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader kicker="CHALLENGE" title="Rhythm anchor" />
                  <CardBody>
                    <BigCue title={studentAnchor.title} subtitle={studentAnchor.subtitle} />
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader kicker="TARGETS" title="Copy the rhythm shape" />
                  <CardBody>
                    <Targets lines={exercise.studentCore ?? []} />
                    {(!exercise.studentCore || exercise.studentCore.length === 0) && (
                      <div className="text-sm text-gray-600 text-center">No target lines yet for this exercise.</div>
                    )}
                  </CardBody>
                </Card>

                {hasExtra && (
                  <Disclosure
                    title="If stuck"
                    subtitle="Quick rescue fixes (open only when something breaks)."
                    open={stuckOpen}
                    onToggle={() => setStuckOpen((v) => !v)}
                  >
                    <ul className="list-disc pl-5 text-gray-700 space-y-2">
                      {(exercise.extraSpecific ?? []).map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </Disclosure>
                )}
              </div>
            )}

            {/* TEACHER */}
            {tab === "teacher" && (
              <div className="space-y-4">
                {challengeList.length > 0 && (
                  <Card>
                    <CardHeader kicker="TEACHER" title="Challenge (what breaks first)" />
                    <CardBody>
                      <ul className="list-disc pl-5 text-gray-900 space-y-2">
                        {challengeList.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </CardBody>
                  </Card>
                )}

                {fixes.length > 0 && (
                  <Card>
                    <CardHeader kicker="TEACHER" title="Fixes (how to repair)" />
                    <CardBody>
                      <ul className="list-disc pl-5 text-gray-900 space-y-2">
                        {fixes.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </CardBody>
                  </Card>
                )}

                {drills.length > 0 && (
                  <Card>
                    <CardHeader kicker="TEACHER" title="Drills (what to do in class)" />
                    <CardBody>
                      <ul className="list-disc pl-5 text-gray-900 space-y-2">
                        {drills.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                      <div className="mt-3 text-xs text-gray-500">
                        Coaching order: fix pauses → fix stress → then fine phonetics.
                      </div>
                    </CardBody>
                  </Card>
                )}

                {extraExercises.length > 0 && (
                  <Disclosure
                    title="Extra exercises"
                    subtitle="Optional expansions once the core is stable."
                    open={extraOpen}
                    onToggle={() => setExtraOpen((v) => !v)}
                  >
                    <ul className="list-disc pl-5 text-gray-700 space-y-2">
                      {extraExercises.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </Disclosure>
                )}

                {challengeList.length === 0 && drills.length === 0 && fixes.length === 0 && extraExercises.length === 0 && (
                  <Card>
                    <CardBody>
                      <div className="text-center text-sm text-gray-600">No teacher notes for this exercise yet.</div>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}

            {/* GLOSSARY */}
            {tab === "glossary" && (
              <div className="space-y-4">
                <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
                  <CardHeader kicker="GLOSSARY" title="Quick definitions" />
                  
                </Card>

                {Glossary ? (
                  <Glossary compact />
                ) : (
                  <Card>
                    <CardBody>
                      <div className="text-center text-sm text-gray-600">No glossary for this course yet.</div>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}

            {/* ABOUT */}
{tab === "about" && (
  <div className="space-y-4">
    <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
      <CardHeader kicker="ABOUT" title="About this course" />
      <CardBody>
        {course?.AboutPanel ? <course.AboutPanel compact /> : null}
      </CardBody>
    </Card>
  </div>
)}
          </div>

          {/* Footer */}
          <div className="border-t border-white/60 bg-white/60 backdrop-blur-sm px-5 py-4 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              type="button"
              className="px-4 py-2 rounded-xl bg-white/80 border border-white/60 hover:bg-white text-sm font-medium text-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
